import React, { useRef, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  Stack,
  Button,
  useTheme,
  alpha,
  Grid,
} from "@mui/material";
import { motion, useInView } from "framer-motion";
import { PlayArrow as PlayIcon } from "@mui/icons-material";
import video from "../../assets/videos/advert.mp4";

const Advert = React.forwardRef((props, ref) => {
  const theme = useTheme();
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: false, margin: "-100px" });

  useEffect(() => {
    if (isInView && videoRef.current) {
      videoRef.current.play().catch((error) => {
        console.log("Video autoplay failed:", error);
      });
    }
  }, [isInView]);

  const handlePlayVideo = () => {
    if (videoRef.current) {
      videoRef.current.play();
    }
  };

  return (
    <Box
      ref={ref}
      sx={{
        py: { xs: 4, md: 6 },
        bgcolor: "background.paper",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "100%",
          background: `linear-gradient(180deg, ${alpha(
            theme.palette.primary.main,
            0.04
          )} 0%, transparent 100%)`,
        },
      }}
    >
      <Container ref={containerRef}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Stack spacing={3}>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  Discover Our Story
                </Typography>
                <Typography variant="body1" sx={{ color: "text.secondary" }}>
                  Watch how we transform ideas into digital reality. Our team of
                  experts combines creativity with technical excellence to
                  deliver outstanding solutions for our clients.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<PlayIcon />}
                  onClick={handlePlayVideo}
                  sx={{
                    alignSelf: "flex-start",
                    px: 4,
                    py: 1.5,
                    "&:hover": {
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  Play Video
                </Button>
              </Stack>
            </motion.div>
          </Grid>

          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <Card
                elevation={0}
                sx={{
                  borderRadius: 4,
                  overflow: "hidden",
                  boxShadow: theme.shadows[8],
                  transition: "transform 0.3s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-8px)",
                  },
                }}
              >
                <Box
                  component="video"
                  ref={videoRef}
                  src={video}
                  autoPlay
                  loop
                  muted
                  playsInline
                  poster="/path/to/poster-image.jpg" // Add a poster image
                  sx={{
                    width: "100%",
                    height: "auto",
                    display: "block",
                    objectFit: "cover",
                  }}
                />
              </Card>
            </motion.div>
          </Grid>
        </Grid>

        {/* Additional Features Section */}
        <Box sx={{ mt: 8 }}>
          <Grid container spacing={3}>
            {[
              {
                title: "Creative Excellence",
                description: "Award-winning designs and innovative solutions",
              },
              {
                title: "Technical Expertise",
                description: "Cutting-edge technology and best practices",
              },
              {
                title: "Client Success",
                description: "Proven track record of delivering results",
              },
            ].map((feature, index) => (
              <Grid item xs={12} md={4} key={feature.title}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      p: 3,
                      height: "100%",
                      transition: "all 0.3s ease-in-out",
                      border: `1px solid ${alpha(
                        theme.palette.primary.main,
                        0.1
                      )}`,
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: theme.shadows[4],
                      },
                    }}
                  >
                    <Stack spacing={2}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {feature.description}
                      </Typography>
                    </Stack>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
});

Advert.displayName = "Advert";

export default Advert;
